import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  useTheme,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { SEOHead, pageSEO } from '../../utils/seo';
import {
  Code as CodeIcon,
  Devices as DevicesIcon,
  CloudDone as CloudIcon,
  Security as SecurityIcon,
  CheckCircle as CheckIcon,
  ArrowForward as ArrowForwardIcon,
  Timeline as TimelineIcon,
  Group as GroupIcon,
  Build as BuildIcon,
} from '@mui/icons-material';

const ServicesPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation('marketing');

  const detailedServices = [
    {
      id: 'web-app',
      title: t('services_detail_web_app_title'),
      subtitle: t('services_detail_web_app_subtitle'),
      description: t('services_detail_web_app_desc'),
      icon: <CodeIcon fontSize="large" />,
      features: [
        t('services_detail_web_app_feature_1'),
        t('services_detail_web_app_feature_2'),
        t('services_detail_web_app_feature_3'),
        t('services_detail_web_app_feature_4'),
        t('services_detail_web_app_feature_5'),
        t('services_detail_web_app_feature_6'),
        t('services_detail_web_app_feature_7'),
        t('services_detail_web_app_feature_8'),
      ],
      technologies: ['React', 'TypeScript', 'Laravel', 'PHP', 'Material-UI', 'MySQL', 'Redis', 'Docker'],
      process: [
        t('services_detail_web_app_process_1'),
        t('services_detail_web_app_process_2'),
        t('services_detail_web_app_process_3'),
        t('services_detail_web_app_process_4'),
        t('services_detail_web_app_process_5'),
      ],
      timeline: t('services_detail_web_app_timeline'),
      teamSize: t('services_detail_web_app_team'),
      testId: 'service-web-app-detail',
    },
    {
      id: 'mobile-app',
      title: t('services_detail_mobile_app_title'),
      subtitle: t('services_detail_mobile_app_subtitle'),
      description: t('services_detail_mobile_app_desc'),
      icon: <DevicesIcon fontSize="large" />,
      features: [
        t('services_detail_mobile_app_feature_1'),
        t('services_detail_mobile_app_feature_2'),
        t('services_detail_mobile_app_feature_3'),
        t('services_detail_mobile_app_feature_4'),
        t('services_detail_mobile_app_feature_5'),
        t('services_detail_mobile_app_feature_6'),
        t('services_detail_mobile_app_feature_7'),
        t('services_detail_mobile_app_feature_8'),
      ],
      technologies: ['React Native', 'Flutter', 'Dart', 'Expo', 'Firebase', 'SQLite', 'Redux', 'Jest'],
      process: [
        t('services_detail_mobile_app_process_1'),
        t('services_detail_mobile_app_process_2'),
        t('services_detail_mobile_app_process_3'),
        t('services_detail_mobile_app_process_4'),
        t('services_detail_mobile_app_process_5'),
      ],
      timeline: t('services_detail_mobile_app_timeline'),
      teamSize: t('services_detail_mobile_app_team'),
      testId: 'service-mobile-app-detail',
    },
    {
      id: 'api-development',
      title: t('services_detail_api_title'),
      subtitle: t('services_detail_api_subtitle'),
      description: t('services_detail_api_desc'),
      icon: <CloudIcon fontSize="large" />,
      features: [
        t('services_detail_api_feature_1'),
        t('services_detail_api_feature_2'),
        t('services_detail_api_feature_3'),
        t('services_detail_api_feature_4'),
        t('services_detail_api_feature_5'),
        t('services_detail_api_feature_6'),
        t('services_detail_api_feature_7'),
        t('services_detail_api_feature_8'),
      ],
      technologies: ['Laravel', 'Node.js', 'GraphQL', 'Docker', 'Redis', 'PostgreSQL', 'JWT', 'Swagger'],
      process: [
        t('services_detail_api_process_1'),
        t('services_detail_api_process_2'),
        t('services_detail_api_process_3'),
        t('services_detail_api_process_4'),
        t('services_detail_api_process_5'),
      ],
      timeline: t('services_detail_api_timeline'),
      teamSize: t('services_detail_api_team'),
      testId: 'service-api-detail',
    },
    {
      id: 'devops',
      title: t('services_detail_devops_title'),
      subtitle: t('services_detail_devops_subtitle'),
      description: t('services_detail_devops_desc'),
      icon: <SecurityIcon fontSize="large" />,
      features: [
        t('services_detail_devops_feature_1'),
        t('services_detail_devops_feature_2'),
        t('services_detail_devops_feature_3'),
        t('services_detail_devops_feature_4'),
        t('services_detail_devops_feature_5'),
        t('services_detail_devops_feature_6'),
        t('services_detail_devops_feature_7'),
        t('services_detail_devops_feature_8'),
      ],
      technologies: ['Docker', 'Kubernetes', 'AWS', 'Azure', 'GitHub Actions', 'Terraform', 'Nginx', 'Monitoring'],
      process: [
        t('services_detail_devops_process_1'),
        t('services_detail_devops_process_2'),
        t('services_detail_devops_process_3'),
        t('services_detail_devops_process_4'),
        t('services_detail_devops_process_5'),
      ],
      timeline: t('services_detail_devops_timeline'),
      teamSize: t('services_detail_devops_team'),
      testId: 'service-devops-detail',
    },
  ];

  const handleGetStarted = (serviceId: string) => {
    // Navigate to streamlined signup with pre-selected technology and service type
    const service = detailedServices.find(s => s.id === serviceId);
    if (service) {
      navigate('/register', { 
        state: { 
          serviceType: serviceId,
          technologies: service.technologies,
          prefilledData: {
            projectType: service.title,
          }
        } 
      });
    }
  };

  const handleContactUs = () => {
    // Navigate to contact form
    navigate('/contact');
  };

  return (
    <>
      <SEOHead {...pageSEO.services} />
      <Box>
      {/* Hero Section */}
      <Box
        component="section"
        data-testid="services-hero"
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          py: { xs: 8, md: 10 },
          mb: 8,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
            <Typography
              variant="h1"
              component="h1"
              data-testid="services-hero-title"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 700,
                mb: 3,
                lineHeight: 1.2,
              }}
            >
              {t('services_page_hero_title')}
            </Typography>
            <Typography
              variant="h5"
              component="h2"
              data-testid="services-hero-subtitle"
              sx={{
                fontSize: { xs: '1.125rem', md: '1.5rem' },
                fontWeight: 400,
                mb: 4,
                opacity: 0.9,
                lineHeight: 1.6,
              }}
            >
              {t('services_page_hero_subtitle')}
            </Typography>
            <Button
              variant="contained"
              size="large"
              data-testid="services-hero-cta"
              onClick={handleContactUs}
              sx={{
                bgcolor: 'white',
                color: theme.palette.primary.main,
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: 'grey.100',
                },
              }}
              endIcon={<ArrowForwardIcon />}
            >
              {t('services_page_hero_cta')}
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Services Details Section */}
      <Container maxWidth="lg">
        <Box component="section" data-testid="services-details" sx={{ mb: 8 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {detailedServices.map((service, index) => (
              <Card
                key={service.id}
                data-testid={service.testId}
                sx={{
                  overflow: 'hidden',
                  boxShadow: theme.shadows[4],
                  '&:hover': {
                    boxShadow: theme.shadows[8],
                  },
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', lg: 'row' },
                  minHeight: { lg: 400 }
                }}>
                  {/* Service Info */}
                  <Box sx={{ flex: 1, p: { xs: 3, md: 4 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 64,
                          height: 64,
                          borderRadius: '50%',
                          bgcolor: `${theme.palette.primary.main}20`,
                          color: theme.palette.primary.main,
                          mr: 3,
                        }}
                      >
                        {service.icon}
                      </Box>
                      <Box>
                        <Typography
                          variant="h4"
                          component="h3"
                          data-testid={`${service.testId}-title`}
                          sx={{ fontWeight: 700, mb: 1 }}
                        >
                          {service.title}
                        </Typography>
                        <Typography
                          variant="h6"
                          color="text.secondary"
                          data-testid={`${service.testId}-subtitle`}
                        >
                          {service.subtitle}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography
                      variant="body1"
                      data-testid={`${service.testId}-description`}
                      sx={{ mb: 4, lineHeight: 1.7, fontSize: '1.1rem' }}
                    >
                      {service.description}
                    </Typography>

                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        {t('services_key_features')}
                      </Typography>
                      <Box 
                        sx={{ 
                          display: 'grid', 
                          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, 
                          gap: 1
                        }}
                      >
                        {service.features.map((feature) => (
                          <Box key={feature} sx={{ display: 'flex', alignItems: 'center' }}>
                            <CheckIcon 
                              sx={{ 
                                color: theme.palette.primary.main, 
                                mr: 1, 
                                fontSize: 20 
                              }} 
                            />
                            <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                              {feature}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                      {service.technologies.map((tech) => (
                        <Chip
                          key={tech}
                          label={tech}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.75rem' }}
                        />
                      ))}
                    </Box>

                    <Button
                      variant="contained"
                      size="large"
                      data-testid={`${service.testId}-cta`}
                      onClick={() => handleGetStarted(service.id)}
                      sx={{ fontWeight: 600 }}
                      endIcon={<ArrowForwardIcon />}
                    >
                      {t('services_get_started')}
                    </Button>
                  </Box>

                  {/* Service Process & Details */}
                  <Box 
                    sx={{ 
                      flex: { lg: '0 0 400px' },
                      bgcolor: 'grey.50',
                      p: { xs: 3, md: 4 },
                      borderLeft: { lg: `1px solid ${theme.palette.divider}` },
                      borderTop: { xs: `1px solid ${theme.palette.divider}`, lg: 'none' }
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                      {t('services_development_process')}
                    </Typography>
                    <List dense sx={{ mb: 4 }}>
                      {service.process.map((step, stepIndex) => (
                        <ListItem key={stepIndex} sx={{ px: 0, py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                bgcolor: theme.palette.primary.main,
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                              }}
                            >
                              {stepIndex + 1}
                            </Box>
                          </ListItemIcon>
                          <ListItemText
                            primary={step}
                            primaryTypographyProps={{
                              variant: 'body2',
                              sx: { fontSize: '0.9rem', lineHeight: 1.4 }
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TimelineIcon sx={{ mr: 2, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {t('services_timeline')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {service.timeline}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <GroupIcon sx={{ mr: 2, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {t('services_team_size')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {service.teamSize}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Card>
            ))}
          </Box>
        </Box>

        {/* Why Choose Us Section */}
        <Box component="section" data-testid="why-choose-us" sx={{ mb: 8 }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              component="h2"
              data-testid="why-choose-title"
              sx={{
                fontSize: { xs: '2rem', md: '2.75rem' },
                fontWeight: 700,
                mb: 2,
                color: 'text.primary',
              }}
            >
              {t('services_why_choose_title')}
            </Typography>
            <Typography
              variant="h6"
              data-testid="why-choose-subtitle"
              sx={{
                color: 'text.secondary',
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              {t('services_why_choose_subtitle')}
            </Typography>
          </Box>

          <Box 
            sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, 
              gap: 4
            }}
          >
            {[
              {
                icon: <BuildIcon fontSize="large" />,
                title: t('services_benefit_1_title'),
                description: t('services_benefit_1_desc'),
              },
              {
                icon: <SecurityIcon fontSize="large" />,
                title: t('services_benefit_2_title'),
                description: t('services_benefit_2_desc'),
              },
              {
                icon: <GroupIcon fontSize="large" />,
                title: t('services_benefit_3_title'),
                description: t('services_benefit_3_desc'),
              },
            ].map((benefit, index) => (
              <Card
                key={index}
                data-testid={`benefit-${index + 1}`}
                sx={{
                  textAlign: 'center',
                  p: 3,
                  height: '100%',
                  boxShadow: theme.shadows[2],
                  '&:hover': {
                    boxShadow: theme.shadows[4],
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: `${theme.palette.primary.main}20`,
                    color: theme.palette.primary.main,
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  {benefit.icon}
                </Box>
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{ fontWeight: 600, mb: 2 }}
                >
                  {benefit.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ lineHeight: 1.6 }}
                >
                  {benefit.description}
                </Typography>
              </Card>
            ))}
          </Box>
        </Box>

        {/* CTA Section */}
        <Box
          component="section"
          data-testid="services-cta"
          sx={{
            textAlign: 'center',
            py: 8,
            px: 4,
            bgcolor: 'grey.50',
            borderRadius: 2,
            mb: 8,
          }}
        >
          <Typography
            variant="h3"
            component="h2"
            data-testid="services-cta-title"
            sx={{
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              fontWeight: 700,
              mb: 2,
              color: 'text.primary',
            }}
          >
            {t('services_cta_title')}
          </Typography>
          <Typography
            variant="h6"
            data-testid="services-cta-subtitle"
            sx={{
              color: 'text.secondary',
              mb: 4,
              maxWidth: 600,
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            {t('services_cta_subtitle')}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              data-testid="services-cta-contact"
              onClick={() => navigate('/register')}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
              }}
              endIcon={<ArrowForwardIcon />}
            >
              {t('services_cta_button')}
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
    </>
  );
};

export default ServicesPage;